import clsx from 'clsx';
import { type FC,useMemo } from 'react';

import { ApiError } from '../../api/apiError';

export interface ErrorSwitchOption {
    errorName?: string;
    reasonCode?: string;
    returnCode?: string;
    message: string;
    showDetails?: boolean;
}

export interface ErrorSwitchProps {
    error?: Error|ApiError|string|null;
    options: ErrorSwitchOption[];
    default: Omit<ErrorSwitchOption, 'errorName'|'reasonCode'|'returnCode'>;
    details?: unknown;
    retry?: () => void;
    className?: string;
}

export const ErrorSwitch: FC<ErrorSwitchProps> = ({
    error,
    options,
    default: defaultOption,
    details,
    retry,
    className
}) => {
    const option = useMemo(() => {
        if (!error) {
            return null;
        }

        const option = options.find(o => {
            if (error instanceof ApiError) {
                const _details = error.details as { reasonCode?: string, returnCode?: string };

                return (
                    error.name === o.errorName ||
                    (o.reasonCode && _details?.reasonCode === o.reasonCode) ||
                    (o.returnCode && _details?.returnCode === o.returnCode)
                );
            } else if (error instanceof Error) {
                return error.name === o.errorName;
            } else {
                return error === o.errorName;
            }
        });

        return option ?? defaultOption;
    }, [error, options, defaultOption]);

    if (!option) {
        return;
    }

    return (
        <div className={clsx(
            'flex flex-col gap-1',
            className
        )}>
            <span className='text-sm font-medium text-red-900'>{option.message}</span>
            {(option.showDetails && !!details) && (
                <span className='text-xs text-gray-500'>{typeof details === 'string' ? details : JSON.stringify(details)}</span>
            )}
            {retry && (
                <button onClick={retry} className='text-xs hyperlink'>Zkusit znovu</button>
            )}
        </div>
    );
};
